import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import ChatWindow from "./ChatWindow";
import "./Chatbot.css";

const STORAGE_KEY = "chatbot_btn_pos";
const DRAG_THRESHOLD = 6; // px before a press counts as a drag (not a click)
const BTN_SIZE = 70;
const EDGE_MARGIN = 8;

const ChatbotLauncher = () => {
  const [isMinimized, setIsMinimized] = useState(true);
  const location = useLocation();

  // Parked position of the (minimized) button. null => default CSS corner.
  const [pos, setPos] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const btnRef = useRef(null);
  const drag = useRef({
    active: false,
    moved: false,
    startX: 0,
    startY: 0,
    baseLeft: 0,
    baseTop: 0,
  });

  const clamp = (left, top) => {
    const maxLeft = window.innerWidth - BTN_SIZE - EDGE_MARGIN;
    const maxTop = window.innerHeight - BTN_SIZE - EDGE_MARGIN;
    return {
      left: Math.max(EDGE_MARGIN, Math.min(left, maxLeft)),
      top: Math.max(EDGE_MARGIN, Math.min(top, maxTop)),
    };
  };

  // Keep a parked button on-screen when the viewport is resized.
  useEffect(() => {
    if (!pos) return;
    const onResize = () => setPos((p) => (p ? clamp(p.left, p.top) : p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [pos]);

  const onPointerDown = (e) => {
    if (!isMinimized) return; // only park while minimized
    const rect = btnRef.current.getBoundingClientRect();
    drag.current = {
      active: true,
      moved: false,
      startX: e.clientX,
      startY: e.clientY,
      baseLeft: rect.left,
      baseTop: rect.top,
    };
    btnRef.current.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    const dy = e.clientY - drag.current.startY;
    if (!drag.current.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    drag.current.moved = true;
    setPos(clamp(drag.current.baseLeft + dx, drag.current.baseTop + dy));
  };

  const onPointerUp = (e) => {
    if (!drag.current.active) return;
    drag.current.active = false;
    btnRef.current.releasePointerCapture?.(e.pointerId);
    if (drag.current.moved) {
      // Clone so React re-renders (restores transition/animation after drag).
      setPos((p) => {
        if (!p) return p;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
        return { ...p };
      });
    }
  };

  const onClick = () => {
    // A finished drag also fires a click — ignore it so we don't toggle.
    if (drag.current.moved) {
      drag.current.moved = false;
      return;
    }
    setIsMinimized((prev) => !prev);
  };

  if (location.pathname === "/login") {
    return null;
  }

  // Apply the parked position only while minimized; when open, the button
  // returns to its default corner so it stays anchored to the chat window.
  const buttonStyle = {
    touchAction: "none",
    // Track the cursor 1:1 while dragging (the CSS transition would lag it).
    ...(drag.current.active ? { transition: "none", animation: "none" } : {}),
    ...(isMinimized && pos
      ? { left: pos.left, top: pos.top, right: "auto", bottom: "auto" }
      : {}),
  };

  return (
    <>
      {/* Chat Window (always mounted, just minimized when needed) */}
      <div
        className={`chatbot-container ${isMinimized ? "minimized" : "open"}`}
      >
        <ChatWindow onClose={() => setIsMinimized(true)} />
      </div>

      {/* Floating Button (drag to move, click to open) */}
      <button
        ref={btnRef}
        className={`chatbot-button ${!isMinimized ? "chat-open" : ""}`}
        style={buttonStyle}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onClick={onClick}
        title={isMinimized ? "Open Chat (drag to move)" : "Close Chat"}
      >
        {isMinimized ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.17L4 17.17V4H20V16ZM7 9H17V11H7V9ZM7 12H15V14H7V12Z" fill="currentColor"/>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
          </svg>
        )}
      </button>
    </>
  );
};

export default ChatbotLauncher;
